// ============================================================
// MOVEMENT RESOLUTION — Step B of each Tick (SRS §3.3.3)
// ============================================================
//
// Two sub-phases:
//   sub-phase 0: every unit tries to move 1 step
//   sub-phase 1: only units with REMAINING budget try 1 more step
//                (Session 12 follow-up: puddle no longer reduces budget;
//                 only swamp does. Cavalry walks through puddles at full
//                 speed = 2.)
//
// 5-level movement priority (per unit, per sub-phase):
//   (1) enemy in attack range          → don't move
//   (2) path waypoint remaining        → follow path
//   (3) path done                       → step toward enemy base
//       (Session 12 follow-up #3: aggro chase removed; units no
//        longer pursue attackers that left their range)
//   (4) next tile has enemy unit       → stop (combat step handles it)
//   (5) next tile has friendly unit    → allowed (friendly stacking)
//
// Rooted units (Session 11, Leader CCC Grow spell): if `u.rooted === true`
// (cached scalar from a `'rooted'` UnitModifier), the unit is skipped
// entirely in both sub-phases. The flag self-expires when its modifier's
// `ticksRemaining` hits 0 (decremented end-of-tick in tick.ts).
//
// Boulder terrain (Session 12, Leader CCC Rockfall spell): `'boulder'`
// cells are impassable. If a unit's chosen next step would land on a
// boulder, `_collectIntents` tries the OTHER axis (swap-axis detour);
// if that also hits a boulder or any other blocker, the unit stays put
// for this sub-phase. Path-mode and auto-advance both use the same
// detour because the underlying `_getNextStep` returns a single-axis
// step in either mode.
//
// Base tiles are impassable. Enemy-team units cannot share a tile
// with our target — if they try, the tile becomes contested (both
// enter it for animation, lower-HP retreats in Step D).
//
// Puddle terrain (Session 12 follow-up):
//   - REMOVED — puddles no longer slow cavalry. The previous "-1 budget
//     when stepping on/off a puddle" rule was deleted; puddles now
//     interact ONLY with Lightning (still gets `LIGHTNING_PUDDLE_BONUS`).
//   - Swamp keeps its own slowdown (see below) and shares the
//     `_decrementBudget` machinery for stepping-into penalties.
//
// Swamp terrain (Session 11 follow-up):
//   - Cavalry (speed≥2) on swamp at tick start: budget = 1 (same as
//     puddle, but unconditional — no +/-1 math). Sub-phase 1 disabled.
//   - Infantry / Archer (speed=1) on swamp at tick start: budget
//     alternates between 0 and 1 across consecutive swamp ticks via
//     `BattleUnit.swampSkipPending`. Net half speed.
//
//     SEMANTIC (post-fix #2 — first swamp tick must skip):
//     • Off-swamp ticks PRIME the flag to true ("ready to skip on
//       next swamp tick"). This makes "ground turning to swamp under
//       a unit" or "walking onto swamp" both result in a SKIP next tick.
//     • pending=true  → budget=0, set pending=false (SKIP this tick)
//       — bypass clamp so speed-1 clamp doesn't force budget back to 1.
//     • pending=false → budget=1, set pending=true (MOVE this tick)
//
//     With initial pending=true on every newly-built unit and on every
//     off-swamp tick, the pattern is ALWAYS: skip → move → skip → move
//     starting from the first encounter. Total = 1 step per 2 ticks
//     across any continuous swamp stay.
//   - Stepping INTO a swamp during sub-phase 0: -1 budget AFTER the
//     step (same as puddle); means a cavalry can't sub-phase-1 onto
//     swamp.
//   - When unit is NOT on swamp at tick start, `swampSkipPending` is
//     reset to true so the next swamp encounter starts on the SKIP step.
//
// Mutates `units` in place. Returns the recorded moves and contested
// tiles so the StateManager can emit a MoveEvent.

import type { BattleUnit, Base, Pos } from '../BattleUnit.ts';
import { BATTLE_COLS, BATTLE_ROWS, UCFG } from '../constants.ts';
import { cd, md, clamp } from '../utils.ts';
import { getTerrainAt, type TerrainGrid } from './terrain.ts';
import type { MoveRecord, ContestedRecord } from './types.ts';

export interface MovementResult {
  moves: MoveRecord[];
  contested: ContestedRecord[];
}

/**
 * Resolve Step B movement.
 *
 * `terrain` is forwarded to swamp-step decrements + boulder detour
 * checks. Puddle slowdown was removed (Session 12 follow-up) so puddle
 * cells no longer reduce the per-tick step budget.
 */
export function resolveMovement(
  units: BattleUnit[],
  bases: Base[],
  terrain?: TerrainGrid,
): MovementResult {
  const moves: MoveRecord[] = [];
  const contested: ContestedRecord[] = [];
  const contestedUnitIds = new Set<string>();

  // Mirror BattleDirector: track path advancement locally while mutating
  // unit positions eagerly. Applying in place gives the next intent the
  // updated occupancy map automatically.
  const pathIdxMap = new Map<string, number>();
  for (const u of units) if (!u.dead) pathIdxMap.set(u.id, u.pathIdx);

  // Per-unit step budget (b 段). Built once at tick start; mutated as
  // units step. `_collectIntents` filters by stepsRemaining > 0 so the
  // 2-sub-phase loop dynamically respects each unit's remaining budget.
  const stepsRemaining = new Map<string, number>();
  for (const u of units) {
    if (u.dead) continue;
    const cfg = UCFG[u.type];
    let budget = cfg.speed;
    const startCell = terrain ? getTerrainAt(terrain, u.pos.x, u.pos.y) : 'none';
    let bypassClamp = false;  // set true ONLY for swamp-skip (need budget=0)

    if (startCell !== 'swamp') {
      // Off-swamp PRIMES the skip flag to true so the very next swamp
      // tick starts on a SKIP, not a free first move. This matches user
      // expectation: "ground became swamp under my infantry → next
      // tick they can't move." Without this prime, the pattern would
      // start with a move and feel like "swamp doesn't slow me at all
      // until 2 ticks later".
      u.swampSkipPending = true;
    }

    if (startCell === 'swamp') {
      if (cfg.speed >= 2) {
        // Cavalry: half speed (2 → 1). Same shape as puddle but
        // unconditional so the clamp logic doesn't matter.
        budget = 1;
      } else {
        // Speed-1 units: alternate move-skip-move-skip across consecutive
        // swamp ticks. Use the per-unit pending flag as the state mark.
        if (u.swampSkipPending) {
          budget = 0;
          bypassClamp = true;       // allow budget=0; clamp would force 1
          u.swampSkipPending = false; // next swamp tick will move
        } else {
          budget = 1;
          u.swampSkipPending = true;  // next swamp tick will skip
        }
      }
    }

    // Clamp at ≥1 if base speed is ≥1 (every alive unit can take at
    // least one step per tick — see file header). Swamp-skip explicitly
    // bypasses this so speed-1 units can actually skip.
    if (!bypassClamp && cfg.speed >= 1 && budget < 1) budget = 1;
    stepsRemaining.set(u.id, budget);
  }

  for (let sp = 0; sp < 2; sp++) {
    const intents = _collectIntents(
      units, bases, sp, contestedUnitIds, pathIdxMap, stepsRemaining, terrain,
    );
    const byTile = new Map<string, typeof intents>();
    for (const intent of intents) {
      const key = `${intent.to.x},${intent.to.y}`;
      if (!byTile.has(key)) byTile.set(key, []);
      byTile.get(key)!.push(intent);
    }

    for (const [, entries] of byTile) {
      const teams = new Set(entries.map((e) => e.unit.team));
      if (entries.length === 1) {
        const { unit, from, to } = entries[0];
        moves.push({ unitId: unit.id, from, to, subPhase: sp });
        unit.pos = { ...to };
        _advancePathIfMatched(unit, to, pathIdxMap);
        _decrementBudget(unit, to, terrain, stepsRemaining);
      } else if (teams.size > 1) {
        // Contested: pick one unit per team to occupy the tile.
        const t0 = entries.find((e) => e.unit.team === 0)!;
        const t1 = entries.find((e) => e.unit.team === 1)!;
        const pos = { ...t0.to };
        moves.push({ unitId: t0.unit.id, from: t0.from, to: pos, subPhase: sp });
        moves.push({ unitId: t1.unit.id, from: t1.from, to: pos, subPhase: sp });
        contested.push({
          pos,
          unitAId: t0.unit.id,
          unitAFrom: t0.from,
          unitBId: t1.unit.id,
          unitBFrom: t1.from,
        });
        contestedUnitIds.add(t0.unit.id);
        contestedUnitIds.add(t1.unit.id);
        t0.unit.pos = { ...pos };
        t1.unit.pos = { ...pos };
        _advancePathIfMatched(t0.unit, pos, pathIdxMap);
        _advancePathIfMatched(t1.unit, pos, pathIdxMap);
        _decrementBudget(t0.unit, pos, terrain, stepsRemaining);
        _decrementBudget(t1.unit, pos, terrain, stepsRemaining);
        // Any additional same-tile entries from other teams are dropped
        // (matches BattleDirector's "extra units don't move" semantics).
      } else {
        // Same team — all move in (friendly stacking).
        for (const e of entries) {
          moves.push({ unitId: e.unit.id, from: e.from, to: e.to, subPhase: sp });
          e.unit.pos = { ...e.to };
          _advancePathIfMatched(e.unit, e.to, pathIdxMap);
          _decrementBudget(e.unit, e.to, terrain, stepsRemaining);
        }
      }
    }
  }

  // Flush final path indices back onto the units.
  for (const u of units) {
    if (!u.dead && pathIdxMap.has(u.id)) u.pathIdx = pathIdxMap.get(u.id)!;
  }

  return { moves, contested };
}

/**
 * Apply a step's budget cost: 1 for the step itself, plus another 1 if
 * the destination tile is swamp (Session 12: puddle no longer slows).
 * No clamp here — sub-phase 2 is gated on `> 0`, so any negative budget
 * naturally suppresses further movement.
 */
function _decrementBudget(
  unit: BattleUnit,
  to: Pos,
  terrain: TerrainGrid | undefined,
  stepsRemaining: Map<string, number>,
): void {
  let remaining = (stepsRemaining.get(unit.id) ?? 0) - 1;
  if (terrain) {
    const cell = getTerrainAt(terrain, to.x, to.y);
    // Stepping into swamp gobbles an extra budget point so sub-phase 1
    // is suppressed (cavalry can't dash past swamp). Puddle slowdown
    // was removed Session 12 follow-up.
    if (cell === 'swamp') remaining -= 1;
  }
  stepsRemaining.set(unit.id, remaining);
}

// ---------------------------------------------------------------
// Intent collection
// ---------------------------------------------------------------

interface Intent {
  unit: BattleUnit;
  from: Pos;
  to: Pos;
}

function _collectIntents(
  units: BattleUnit[],
  bases: Base[],
  subPhase: number,
  frozenIds: Set<string>,
  pathIdxMap: Map<string, number>,
  stepsRemaining: Map<string, number>,
  terrain?: TerrainGrid,
): Intent[] {
  const intents: Intent[] = [];
  for (const u of units) {
    if (u.dead) continue;
    if (frozenIds.has(u.id)) continue;
    // Session 11 — rooted units (Grow spell on pre-existing grass) skip
    // BOTH sub-phases entirely. Check before the speed/budget filters so
    // there's a single early-exit path and the intent is grep-friendly.
    if (u.rooted) continue;
    // Sub-phase 1 only ever applies to base-speed ≥ 2 units (the
    // architecture is hard-wired to 2 sub-phases). A speed-1 unit that
    // failed to move in sub-phase 0 (e.g. blocked by a now-departed
    // peer) doesn't get a do-over here.
    if (subPhase === 1 && UCFG[u.type].speed < 2) continue;
    // Dynamic budget gates sub-phase 1 for speed-≥2 units. A unit's
    // budget is cfg.speed minus swamp encounters (start cell + entered
    // cell), clamped to ≥1. Sub-phase 0 always sees budget > 0 because
    // of the clamp, so this filter is effectively a sub-phase 1 check
    // for cavalry slowed by swamp.
    if ((stepsRemaining.get(u.id) ?? 0) <= 0) continue;

    // Priority 1: enemy in range → don't move
    if (_hasEnemyInRange(u, units, bases)) continue;

    let localIdx = pathIdxMap.get(u.id) ?? 0;
    // Session 12 follow-up #3 — auto-consume dead waypoints. A waypoint
    // is "dead" when the unit can never physically land on it: terrain
    // turned to `'boulder'` after a Rockfall, or a base tile somehow
    // appears on the path. Without this, `_stepToward` keeps aiming at
    // the dead cell and pulls the unit back into the boulder it just
    // detoured around → oscillation. Matches the user-facing model
    // "走過就消失" (walked OR bypassed-because-unreachable both count).
    while (localIdx < u.path.length) {
      const w = u.path[localIdx];
      const onBoulder = terrain && getTerrainAt(terrain, w.x, w.y) === 'boulder';
      const onBase = _isBaseTile(w, bases);
      if (!onBoulder && !onBase) break;
      localIdx++;
    }
    pathIdxMap.set(u.id, localIdx);

    let nextPos = _getNextStep(u, u.pos, localIdx, units, bases);
    if (!nextPos) continue;
    if (nextPos.x === u.pos.x && nextPos.y === u.pos.y) continue;

    // Session 12 — boulder detour via BFS. If the chosen next step is a
    // boulder, run BFS from curPos to the priority-ordered goal sets
    // (remaining path waypoints → enemy base adjacents). Only returns
    // null when ALL goal sets are unreachable — i.e. unit is walled in.
    if (terrain && getTerrainAt(terrain, nextPos.x, nextPos.y) === 'boulder') {
      const alt = _detourAroundBoulder(u, u.pos, localIdx, units, bases, nextPos, terrain);
      if (!alt) continue;
      nextPos = alt;
    }

    // Priority 5: enemy occupying next tile → stop
    const enemyBlocking = units.find(
      (e) => !e.dead && e.team !== u.team && e.pos.x === nextPos.x && e.pos.y === nextPos.y,
    );
    if (enemyBlocking) continue;

    // Base tiles are impassable regardless of team
    if (_isBaseTile(nextPos, bases)) continue;

    intents.push({ unit: u, from: { ...u.pos }, to: { ...nextPos } });
  }
  return intents;
}

/**
 * Session 12 follow-up #2 — boulder detour. The primary `_getNextStep`
 * returned `blocked`, a boulder cell. Run a BFS from `curPos` so the
 * unit ALWAYS finds a route as long as one exists; the unit only stops
 * when every path to the enemy base is walled off (the strict spec:
 * "除非所有通往敵方基地的路徑都被擋住").
 *
 * Goal sets are tried in priority order:
 *   1. Remaining path waypoints (`u.path[localIdx..]`). Skips waypoints
 *      that landed on impassable cells (e.g. a boulder dropped on the
 *      drawn path) so the unit advances to the next reachable one.
 *   2. Enemy base — Manhattan-1 neighbours of any base tile (base tiles
 *      themselves are impassable; combat fires once a unit is adjacent).
 *
 * Session 12 follow-up #3 — aggro chase removed entirely. Units no
 * longer pursue attackers that left their range; they fall straight
 * through to enemy-base routing once the planned path is done.
 *
 * Passability: a cell is passable iff it's in-bounds, NOT `'boulder'`,
 * NOT a base tile, and NOT occupied by an enemy unit. Friendly units
 * are passable (matches the regular priority-6 friendly-stacking rule).
 *
 * Returns the first step on the BFS shortest path to whichever goal
 * set yields the closest reachable cell. Returns null only when no
 * goal in any set is reachable — i.e. the unit is truly walled in.
 *
 * BFS is deterministic (fixed neighbour expansion order: up, down,
 * right, left — biased toward forward progress like `_stepToward`).
 * Two equally-short paths resolve to the same first step every run, so
 * PvP replays and tests stay stable without needing a coin-flip.
 */
function _detourAroundBoulder(
  u: BattleUnit,
  curPos: Pos,
  localIdx: number,
  units: BattleUnit[],
  bases: Base[],
  _blocked: Pos,
  terrain: TerrainGrid,
): Pos | null {
  const passable = (x: number, y: number): boolean => {
    if (x < 0 || x >= BATTLE_COLS) return false;
    if (y < 0 || y >= BATTLE_ROWS) return false;
    if (getTerrainAt(terrain, x, y) === 'boulder') return false;
    for (const b of bases) {
      if (b.destroyed) continue;
      for (const t of b.tiles) {
        if (t.x === x && t.y === y) return false;
      }
    }
    for (const e of units) {
      if (e.dead) continue;
      if (e.team !== u.team && e.pos.x === x && e.pos.y === y) return false;
    }
    return true;
  };

  const goalSets: Pos[][] = [];

  // (1) Remaining path waypoints that are themselves reachable cells.
  //     Boulders dropped on top of a planned waypoint don't deadlock —
  //     the unit just rolls forward to the next still-reachable one.
  const waypointGoals: Pos[] = [];
  for (let i = localIdx; i < u.path.length; i++) {
    const w = u.path[i];
    if (passable(w.x, w.y)) waypointGoals.push(w);
  }
  if (waypointGoals.length > 0) goalSets.push(waypointGoals);

  // (2) Enemy base — Manhattan-1 neighbours of any base tile.
  const enemyBase = bases.find((b) => !b.destroyed && b.team !== u.team);
  if (enemyBase) {
    const baseAdj: Pos[] = [];
    for (const t of enemyBase.tiles) {
      for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) {
        const nx = t.x + dx;
        const ny = t.y + dy;
        if (passable(nx, ny)) baseAdj.push({ x: nx, y: ny });
      }
    }
    if (baseAdj.length > 0) goalSets.push(baseAdj);
  }

  for (const goals of goalSets) {
    const step = _bfsFirstStep(curPos, goals, passable);
    if (step) return step;
  }
  return null; // truly walled in — no route to any goal exists
}

/**
 * Breadth-first search from `start` to any cell in `goals`. Returns the
 * FIRST step on the shortest path (the cell adjacent to `start` that
 * the unit should move into this tick). Returns null if `start` is
 * already at a goal cell (no movement needed — caller's priority-1
 * enemy-in-range check handles this), or if no goal is reachable.
 *
 * Neighbour expansion order is fixed (up → down → right → left) so the
 * BFS is deterministic. With ties, the first goal discovered wins;
 * "up" is tried first because it's the direction of forward progress
 * toward the enemy base for team-0 units (and for team-1 units the
 * world is flipped on the wire so they see their forward as down,
 * which is the second probe — only one extra BFS layer in the worst
 * case, still cheap on the 8×8 grid).
 */
function _bfsFirstStep(
  start: Pos,
  goals: Pos[],
  passable: (x: number, y: number) => boolean,
): Pos | null {
  if (goals.length === 0) return null;
  const goalSet = new Set(goals.map((g) => `${g.x},${g.y}`));
  const startKey = `${start.x},${start.y}`;
  if (goalSet.has(startKey)) return null;

  const visited = new Set<string>([startKey]);
  const parent = new Map<string, string>();
  const queue: Pos[] = [start];
  const DIRS: ReadonlyArray<readonly [number, number]> = [
    [0, -1], [0, 1], [1, 0], [-1, 0],
  ];

  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const [dx, dy] of DIRS) {
      const nx = cur.x + dx;
      const ny = cur.y + dy;
      const key = `${nx},${ny}`;
      if (visited.has(key)) continue;
      if (!passable(nx, ny)) continue;
      visited.add(key);
      parent.set(key, `${cur.x},${cur.y}`);
      if (goalSet.has(key)) {
        // Backtrack to the first step from `start`.
        let cKey = key;
        while (parent.get(cKey) !== startKey) {
          cKey = parent.get(cKey)!;
        }
        const [sx, sy] = cKey.split(',').map(Number);
        return { x: sx, y: sy };
      }
      queue.push({ x: nx, y: ny });
    }
  }
  return null;
}

function _getNextStep(
  u: BattleUnit,
  curPos: Pos,
  localIdx: number,
  _units: BattleUnit[],
  bases: Base[],
): Pos | null {
  const hasPath = localIdx < u.path.length;
  if (hasPath) {
    // Session 10 — single-axis step TOWARD the next waypoint, NOT the
    // waypoint itself. For the normal case (waypoint is Manhattan-
    // adjacent because the deploy-time rule §4.3 enforces it) this
    // returns the waypoint unchanged — behavior is identical to before.
    //
    // For the displaced case (post-whirlwind: unit is no longer next to
    // the waypoint) this returns ONE step toward the waypoint instead
    // of teleporting straight there. The unit walks back onto its path
    // over 1-2 ticks. `_advancePathIfMatched` only bumps `pathIdx` when
    // the step lands exactly on the waypoint, so we organically re-
    // acquire the path without skipping waypoints.
    return _stepToward(curPos, u.path[localIdx]);
  }

  // Session 12 follow-up #3 — aggro chase removed. Once the planned
  // path is done, units head STRAIGHT to the enemy base. Combat still
  // fires when an enemy is in attack range (Priority 1), but a unit
  // that was attacked and lost its attacker out of range no longer
  // pursues — it keeps marching toward the base.
  return _stepTowardEnemyBase(u, curPos, bases);
}

function _stepTowardEnemyBase(u: BattleUnit, curPos: Pos, bases: Base[]): Pos | null {
  const enemyBase = bases.find((b) => !b.destroyed && b.team !== u.team);
  if (!enemyBase) return null;
  let nearest = enemyBase.tiles[0];
  let bestD = md(curPos, nearest);
  for (const t of enemyBase.tiles) {
    const d = md(curPos, t);
    if (d < bestD) { bestD = d; nearest = t; }
  }
  return _stepToward(curPos, nearest);
}

/**
 * Single-step movement toward `to`. Units may ONLY move orthogonally
 * (up / down / left / right) — never diagonally. This matches the
 * deploy-time path rule (paths are Manhattan-adjacent, §4.3) and
 * gives a predictable feel: a soldier walking toward the enemy base
 * after exhausting their drawn path commits to one axis at a time.
 *
 * Tie-breaker when both axes need to close distance: prefer Y (the
 * axis pointing at the enemy base). When dy = 0 we step on X. When
 * already at `to` we stay put.
 */
function _stepToward(from: Pos, to: Pos): Pos {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  // Stay put if already aligned on both axes.
  if (dx === 0 && dy === 0) return { x: from.x, y: from.y };
  // Prefer Y when |dy| ≥ |dx|; otherwise step on X.
  if (dy !== 0 && Math.abs(dy) >= Math.abs(dx)) {
    return {
      x: from.x,
      y: clamp(from.y + Math.sign(dy), 0, BATTLE_ROWS - 1),
    };
  }
  return {
    x: clamp(from.x + Math.sign(dx), 0, BATTLE_COLS - 1),
    y: from.y,
  };
}

function _hasEnemyInRange(u: BattleUnit, units: BattleUnit[], bases: Base[]): boolean {
  const cfg = UCFG[u.type];
  for (const e of units) {
    if (e.dead || e.team === u.team) continue;
    if (cd(u.pos, e.pos) <= cfg.range) return true;
  }
  for (const b of bases) {
    if (b.destroyed || b.team === u.team) continue;
    for (const t of b.tiles) {
      if (cd(u.pos, t) <= cfg.range) return true;
    }
  }
  return false;
}

function _isBaseTile(pos: Pos, bases: Base[]): boolean {
  for (const b of bases) {
    if (b.destroyed) continue;
    for (const t of b.tiles) {
      if (t.x === pos.x && t.y === pos.y) return true;
    }
  }
  return false;
}

function _advancePathIfMatched(u: BattleUnit, to: Pos, pathIdxMap: Map<string, number>): void {
  const idx = pathIdxMap.get(u.id) ?? 0;
  if (idx >= u.path.length) return;
  const wp = u.path[idx];
  if (wp && to.x === wp.x && to.y === wp.y) {
    pathIdxMap.set(u.id, idx + 1);
  }
}
